import {
  Alert,
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material'
import type React from 'react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Modal } from '../../../../customComponents/Modal'
import { AttachMoney } from '@mui/icons-material'
import {
  AddStartCommunityOfPractice,
  GetTrainings,
  UpdateStartCommunityOfPractice
} from '../../../../../services/AnnualPlanService'
import { ActivityEditSession } from '../ActivityEditSession'
import { TrainingType, type Training } from '../../../../../models/Training'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { BudgetThresholdExceededModal } from '../../BudgetExceededModal'
import { budgetNeedsReview } from '../../../../../utils/budgetUtil'
import { handleApiResponse } from '../../../../../utils/apiHelpers'

export interface AddStartCommunityOfPracticeFormValues {
  priorityId: number
  trainingId: number | null
  numberOfAttendees: number | null
  budgetRequest: string
  isImplementationPhase: boolean
}

export interface UpdateStartCommunityOfPracticeFormValues extends AddStartCommunityOfPracticeFormValues {
  id: number
  isUpdate: boolean
}

interface Props {
  open: boolean
  annualPlanId: number
  priorityId: number
  initialValues?: UpdateStartCommunityOfPracticeFormValues
  onClose: () => void
  onSaved: () => void
  isImplementationPhase: boolean
  usedIds: number[]
}

type ConfirmState = 'none' | 'needsConfirmation' | 'confirmed'

export const StartCommunityOfPracticeModal = (props: Props): JSX.Element => {
  const isEditing = props?.initialValues != null
  const [confirmState, setConfirmState] = useState<ConfirmState>('none')
  const [formValues, setFormValues] = useState<AddStartCommunityOfPracticeFormValues>({
    priorityId: props.priorityId ?? 0,
    trainingId: null,
    numberOfAttendees: null,
    budgetRequest: '',
    isImplementationPhase: props.isImplementationPhase
  })
  const [submitted, setIsSubmitted] = useState(false)
  const [copTrainingOptions, setCopTrainingOptions] = useState<Training[]>([])

  useEffect(() => {
    const fetchCopTrainings = async (): Promise<void> => {
      const trainings = await handleApiResponse(GetTrainings(TrainingType.CommunityOfPractice, props.annualPlanId))
      if (trainings == null) return
      const filtered = trainings.filter(t => !props.usedIds.includes(t.id ?? -1) || (props.initialValues != null && t.id === props.initialValues.trainingId))
      setCopTrainingOptions(filtered)
    }

    void fetchCopTrainings()
  }, [])

  useEffect(() => {
    if (props.initialValues != null) {
      setFormValues(props.initialValues)
    }
  }, [props.initialValues])

  const hasInvalidFormValues = formValues.trainingId == null ||
    formValues.numberOfAttendees == null ||
    formValues.budgetRequest == null

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const handleSubmit = async (): Promise<void> => {
    if (props.initialValues != null) {
      if (budgetNeedsReview(props.initialValues.budgetRequest, formValues.budgetRequest)) {
        setConfirmState('needsConfirmation')
        return
      }
    }
    await continueSubmit()
  }

  const continueSubmit = async (): Promise<void> => {
    setIsSubmitted(true)
    if (hasInvalidFormValues) {
      toast.error('Please fill out required fields')
      return
    }

    const { success, errors } = props.initialValues != null
      ? await UpdateStartCommunityOfPractice({ ...formValues, id: props.initialValues.id, isUpdate: props.initialValues.isUpdate })
      : await AddStartCommunityOfPractice(formValues)

    if (!success) {
      errors.forEach((error) => toast.error(error))
      return
    }

    toast.success(`Attendance Plan ${isEditing ? 'Updated' : 'Saved'}`)

    props.onClose()
    props.onSaved()
  }

  return (confirmState === 'needsConfirmation'
    ? <BudgetThresholdExceededModal
        onClose={() => {
          setConfirmState('none')
          props.onClose()
        }}
        onConfirm={() => {
          setConfirmState('confirmed')
          void continueSubmit()
        }}
    />
    : <Modal
        title={`${isEditing ? 'Update' : 'Add'} START Community of Practice Attendance Plan`}
        open={props.open}
        data-testid={isEditing ? 'editStartCommunityOfPracticeModal' : 'addStartCommunityOfPracticeModal'}
        width='480px'
        confirmButtonText='Save'
        cancelButtonText='Cancel'
        onClose={props.onClose}
        onConfirm={handleSubmit}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {
              props.isImplementationPhase && props.initialValues == null &&
                <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
                  backgroundColor: 'var(--blue-500)',
                  color: 'white',
                  fill: 'white'
                }} severity="info"
                >The addition of this item will prompt a review from the START team.
                </Alert>
            }
            <Typography variant='h5'>START Communities of Practice</Typography>
            <Typography variant='body1'>Please select the Community of Practice, enter the number of attendees along with the budget request.</Typography>
            <FormControl
              fullWidth
              required={formValues.trainingId == null}
              error={submitted && formValues.trainingId == null}
            >
              <InputLabel id='cop-training-label'>Community of Practice</InputLabel>
              <Select
                disabled={isEditing}
                labelId='cop-training-label'
                id='cop-training'
                value={copTrainingOptions.length > 0 ? formValues.trainingId ?? '' : ''}
                label='Community of Practice'
                name='trainingId'
                data-testid='copTraining'
                onChange={handleFieldUpdate as (e: SelectChangeEvent<typeof formValues.trainingId>) => void}
              >
                {copTrainingOptions.length === 0 && <Typography variant='body1' textAlign='center'>No communities of practice available.</Typography>}
                {copTrainingOptions.map((training) => (
                  <MenuItem key={training.id} value={training.id} data-testid='copTrainingOption'>{training.title}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              name='numberOfAttendees'
              label='Number Attending'
              value={formValues.numberOfAttendees ?? ''}
              data-testid='numberOfAttendees'
              onChange={handleFieldUpdate}
              variant='filled'
              required={formValues.numberOfAttendees == null}
              error={submitted && formValues.numberOfAttendees == null}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Typography variant='body1'>Does this require additional funding to complete?</Typography>

              <FormControl sx={{ width: 'fit-content' }} variant="outlined">
                <InputLabel htmlFor="budget-request">Budget Request</InputLabel>
                <OutlinedInput
                  id="budget-request"
                  startAdornment={
                    <InputAdornment position="start">
                      <AttachMoney />
                    </InputAdornment>
                  }
                  name='budgetRequest'
                  label="Budget Request"
                  value={formValues.budgetRequest}
                  data-testid='budgetRequest'
                  onChange={handleFieldUpdate}
                  required={formValues.budgetRequest === ''}
                  error={submitted && formValues.budgetRequest === ''}
                />
              </FormControl>
            </Box>

            {props.initialValues != null &&
              <ActivityEditSession
                activityId={props.initialValues.id}
                activityName='START Community Of Practice'
                onClose={props.onClose}
              />
            }
          </Box>
        }
    />
  )
}
